import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '../components/PageSEO';
import { FEATURED_PROJECTS, BRAND_ASSETS } from '../data';
import { getFeaturedProjects } from '../lib/cmsClient';
import { ImageLightboxModal, ClickableWorkImage } from '../components/GalleryLightbox';

export default function ProjectsPage() {
  const [projects, setProjects] = useState(FEATURED_PROJECTS);
  const [lightboxState, setLightboxState] = useState({ isOpen: false, index: 0 });

  useEffect(() => {
    async function loadProjects() {
      try {
        const data = await getFeaturedProjects();
        if (data && data.length > 0) {
          setProjects(data);
        }
      } catch (err) {
        console.warn("Could not load projects from CMS:", err);
      }
    }
    loadProjects();
  }, []);

  return (
    <div className="bg-stone-100 min-h-screen">
      <PageSEO
        title="Udvalgte projekter | KTS-Entreprise A/S"
        description="Se eksempler på nogle af de opgaver, vi har udført inden for entreprenørarbejde, kloak, klimasikring og beton."
        ogTitle="Udvalgte projekter | KTS-Entreprise A/S"
        ogDescription="Referencer og projekter udført af KTS-Entreprise i Nordsjælland og Storkøbenhavn."
        ogImage={BRAND_ASSETS.defaultOgImage}
        slug="projekter"
      />

      {/* Header Banner */}
      <section className="bg-stone-900 text-white pt-36 sm:pt-44 md:pt-48 lg:pt-52 pb-12 sm:pb-16 border-b-4 border-[#0072c0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#0072c0]/20 border border-[#0072c0]/40 text-blue-200 text-xs font-heading font-bold uppercase tracking-wider mb-3 rounded-xs">
            <span>Referencer</span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-black uppercase tracking-tight text-white font-heading">
            Udvalgte projekter
          </h1>
          <p className="text-stone-300 text-sm sm:text-base mt-2 max-w-2xl">
            Se eksempler på nogle af de opgaver, vi har udført inden for entreprenørarbejde, kloak, klimasikring og beton.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project) => (
            <div
              key={project.id || project.title}
              className="bg-white border border-stone-200 rounded-xs shadow-xs overflow-hidden flex flex-col justify-between hover:border-[#0072c0]/60 transition-all duration-200 group"
            >
              <div>
                <div className="relative aspect-16/10 bg-stone-100 overflow-hidden">
                  <ClickableWorkImage
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${project.image.replace(/^https:\/\/assets\.auxodev\.dk\//, '')}`}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    onClick={() => setLightboxState({ isOpen: true, index: projects.indexOf(project) })}
                  />
                  {project.category && (
                    <span className="absolute top-3 left-3 bg-[#0072c0] text-white text-[11px] font-heading font-bold uppercase tracking-wider px-2.5 py-1 rounded-xs pointer-events-none">
                      {project.category}
                    </span>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg text-stone-900 uppercase tracking-tight mb-2">
                    {project.title}
                  </h3>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    {project.description}
                  </p>
                </div>
              </div>

              <div className="p-6 pt-0">
                <Link
                  to={project.href || '/projekter'}
                  className="inline-flex items-center justify-center w-full gap-2 bg-stone-900 hover:bg-[#0072c0] text-white font-heading font-bold uppercase tracking-wider py-2.5 px-4 text-xs transition-colors rounded-xs"
                >
                  <span>Se projektet</span>
                  <i className="fa-solid fa-arrow-right text-[10px]"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-white border border-stone-200 p-8 rounded-xs text-center">
          <h3 className="text-xl font-bold uppercase text-stone-900 font-heading mb-2">
            Har du en opgave, vi skal løse?
          </h3>
          <p className="text-stone-600 text-sm max-w-md mx-auto mb-6">
            Kontakt os for en uforpligtende snak om dit næste entreprenør-, jord-, kloak- eller betonprojekt.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center gap-2 bg-[#0072c0] hover:bg-[#005b99] text-white font-heading font-bold uppercase tracking-wider px-6 py-3 text-xs sm:text-sm rounded-xs shadow-xs transition-colors"
          >
            <span>Kontakt os</span>
            <i className="fa-solid fa-arrow-right text-xs"></i>
          </Link>
        </div>
      </div>

      {/* Lightbox for Udvalgte projekter */}
      <ImageLightboxModal
        isOpen={lightboxState.isOpen}
        onClose={() => setLightboxState({ ...lightboxState, isOpen: false })}
        images={projects.map((p) => ({
          src: p.image,
          alt: p.title,
          caption: p.description,
          title: p.title
        }))}
        currentIndex={lightboxState.index}
        onNavigate={(newIdx) => setLightboxState({ ...lightboxState, index: newIdx })}
        title="Udvalgte projekter"
      />
    </div>
  );
}
